import { AgreeToTermsPolicy, Theme } from '@logto/schemas';
import classNames from 'classnames';
import type { TFuncKey } from 'i18next';
import type { ContextType, FunctionComponent, SVGProps } from 'react';
import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';

import ArrowLeftIcon from '@/Layout/AimsLayout/assets/arrow-left.svg?react';
import ChartIcon from '@/Layout/AimsLayout/assets/chart-no-axes-column.svg?react';
import ClipboardCheckIcon from '@/Layout/AimsLayout/assets/clipboard-check.svg?react';
import GraduationCapIcon from '@/Layout/AimsLayout/assets/graduation-cap.svg?react';
import UserPlusIcon from '@/Layout/AimsLayout/assets/user-plus.svg?react';
import PageContext from '@/Providers/PageContextProvider/PageContext';
import TermsAndPrivacyLinks from '@/containers/TermsAndPrivacyLinks';
import DynamicT from '@/shared/components/DynamicT';
import { getBrandingLogoUrl } from '@/shared/utils/logo';
import { layoutClassNames } from '@/utils/consts';

import CustomContent from '../AppLayout/CustomContent';

import aimsLogo from './assets/aims-logo.png';
import studentsPhoto from './assets/students.webp';
import { readHubUrl } from './hub-url';
import styles from './index.module.scss';

type Stage = {
  id: 'admissions' | 'registration' | 'results' | 'graduation';
  title: TFuncKey;
  detail: TFuncKey;
  Icon: FunctionComponent<SVGProps<SVGSVGElement>>;
};

/** The student lifecycle AIMS runs, top to bottom. */
const stages: Stage[] = [
  {
    id: 'admissions',
    title: 'description.aims_layout.admissions',
    detail: 'description.aims_layout.admissions_detail',
    Icon: UserPlusIcon,
  },
  {
    id: 'registration',
    title: 'description.aims_layout.registration',
    detail: 'description.aims_layout.registration_detail',
    Icon: ClipboardCheckIcon,
  },
  {
    id: 'results',
    title: 'description.aims_layout.results',
    detail: 'description.aims_layout.results_detail',
    Icon: ChartIcon,
  },
  {
    id: 'graduation',
    title: 'description.aims_layout.graduation',
    detail: 'description.aims_layout.graduation_detail',
    Icon: GraduationCapIcon,
  },
];

type Branding = NonNullable<ContextType<typeof PageContext>['experienceSettings']>['branding'];

/**
 * The applicant panel (admissions sign-in mockup, signed off 2026-10-25): a cerulean card with the
 * institution's crest and name, the students photo, and the intake. Lines the branding leaves unset
 * are left out. Below 900px it becomes a short header over the form.
 */
const ApplicantPanel = ({
  branding,
  crest,
}: {
  readonly branding: Branding;
  readonly crest?: string;
}) => {
  const { t } = useTranslation();
  const { splitHeadline: name, splitSubtitle, panelKicker, panelTitle, panelText } = branding;

  return (
    <aside className={styles.applicant} data-audience="applicant" aria-label={name ?? undefined}>
      <div aria-hidden className={styles.rings} />
      <div aria-hidden className={classNames(styles.rings, styles.ringsLow)} />
      <div className={styles.applicantBrand}>
        {crest && (
          <span className={styles.crest}>
            <img className={styles.crestImage} src={crest} alt="" />
          </span>
        )}
        <div className={styles.brandText}>
          {name && <b>{name}</b>}
          {splitSubtitle && <span className={styles.subtle}>{splitSubtitle}</span>}
        </div>
      </div>
      <img
        className={styles.photo}
        src={branding.panelImageUrl ?? studentsPhoto}
        alt={t('description.aims_layout.applicant_photo')}
      />
      <div className={styles.intake}>
        {panelKicker && <span className={styles.kicker}>{panelKicker}</span>}
        {panelTitle && <b>{panelTitle}</b>}
        {panelText && <span className={styles.intakeText}>{panelText}</span>}
      </div>
    </aside>
  );
};

/**
 * The AIMS sign-in page: one white canvas on a light grey page. The left panel carries the
 * organisation's crest and name (`branding.splitHeadline`, as on the minimal layout; the
 * organisation override supplies both when the request names an organisation), the four
 * student-lifecycle stages, a faint AIMS mark, and links back to the app that started sign-in
 * ("Hub") and to support — each only when known. The right column centres the form, with the
 * automatic terms line under it and a "Powered by AIMS" pill at the bottom. The panel hides below
 * 900px. The page is light-only, like the approved mockup.
 */
const AimsLayout = () => {
  const { experienceSettings } = useContext(PageContext);
  const hubUrl = useMemo(() => readHubUrl(document.referrer, window.location.origin), []);

  if (!experienceSettings) {
    return null;
  }

  const { branding, supportWebsiteUrl, supportEmail, agreeToTermsPolicy } = experienceSettings;
  // Light-only page: always the light crest.
  const crest = getBrandingLogoUrl({ theme: Theme.Light, branding, isDarkModeEnabled: false });
  const name = branding.splitHeadline;
  const supportHref = supportWebsiteUrl ?? (supportEmail ? `mailto:${supportEmail}` : undefined);

  return (
    <div className={styles.viewBox}>
      <div
        className={classNames(
          styles.canvas,
          branding.aimsAudience === 'applicant' && styles.applicantCanvas,
          layoutClassNames.pageContainer
        )}
      >
        {branding.aimsAudience === 'applicant' ? (
          <ApplicantPanel branding={branding} crest={crest ?? undefined} />
        ) : (
          <aside className={styles.side} aria-label={name ?? undefined}>
            <div className={styles.brand}>
              {crest && <img src={crest} alt="" />}
              {name && <b>{name}</b>}
            </div>
            <ol className={styles.stages}>
              {stages.map(({ id, title, detail, Icon }) => (
                <li key={id} data-stage={id}>
                  <span className={styles.tile}>
                    <Icon aria-hidden />
                  </span>
                  <div>
                    <b>
                      <DynamicT forKey={title} />
                    </b>
                    <span>
                      <DynamicT forKey={detail} />
                    </span>
                  </div>
                </li>
              ))}
            </ol>
            {/* The watermark lives in the stylesheet: the experience CSP blocks inline styles. */}
            <div className={styles.mark} />
            {(hubUrl ?? supportHref) && (
              <nav className={styles.sideFoot}>
                {hubUrl && (
                  <a href={hubUrl}>
                    <ArrowLeftIcon aria-hidden />
                    <DynamicT forKey="description.aims_layout.hub" />
                  </a>
                )}
                {supportHref && (
                  <a href={supportHref} target="_blank" rel="noopener noreferrer">
                    <DynamicT forKey="description.support" />
                  </a>
                )}
              </nav>
            )}
          </aside>
        )}
        <div className={styles.mainColumn}>
          <main className={classNames(styles.main, layoutClassNames.mainContent)}>
            <CustomContent className={layoutClassNames.customContent} />
            <Outlet />
            {/* The pages skip their own read-only terms line on this layout (useLayoutRendersTerms);
                the manual-agreement checkbox stays in the form. */}
            {agreeToTermsPolicy === AgreeToTermsPolicy.Automatic && (
              <TermsAndPrivacyLinks className={styles.terms} />
            )}
          </main>
          <div className={styles.foot}>
            <span className={styles.powered}>
              <DynamicT forKey="description.powered_by" />
              <img className={styles.aimsLogo} src={aimsLogo} alt="AIMS" />
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AimsLayout;
